// The Solo Accuracy pill: how many solo times a student got right, out of how
// many they answered.
//
// The pill shows the fraction itself ("2/3") and takes its colour
// from the accuracy, on the same thresholds the backend uses to pick a math
// level: above 80% green, 30% to 80% inclusive yellow, below 30% red.
//
// Which questions count is governed by the universal rule (see SOLO_ACCURACY_RULE):
// "Solo Accuracy counts the questions you were in the room for when Solo Time ended;
// Team Time answers never change it". The backend is authoritative and
// `useSoloAccuracy` reads those numbers verbatim. The `soloAccuracy` helper
// below is the legacy LLM path and derives its own denominator from `questionsDetail`.

export const SOLO_ACCURACY_RULE =
  "Solo Accuracy counts the questions you were in the room for when Solo Time ended; Team Time answers never change it";

export const READY_TO_LEAD_THRESHOLD = 80;
export const NEEDS_PRACTICE_THRESHOLD = 30;

export const PILL_TONES = {
  green: "green",
  yellow: "yellow",
  red: "red",
};

/**
 * The pill's colour for an accuracy percentage.
 *
 * Boundaries follow the backend: 80 exactly is yellow (mastery is strictly
 * above), 30 exactly is yellow.
 *
 * @param {number|null|undefined} accuracy 0-100.
 * @returns {string|null} A key of PILL_TONES, or null when there is no accuracy.
 */
export const pillTone = (accuracy) => {
  if (accuracy === null || accuracy === undefined || Number.isNaN(accuracy)) return null;
  if (accuracy > READY_TO_LEAD_THRESHOLD) return PILL_TONES.green;
  if (accuracy >= NEEDS_PRACTICE_THRESHOLD) return PILL_TONES.yellow;
  return PILL_TONES.red;
};

/**
 * A student's solo accuracy, counting only the solo times they answered.
 *
 * @param {Array<{result: string}>} questionsDetail Per-question results, each
 *   "correct" | "incorrect" | "no_answer".
 * @returns {{correct: number, answered: number, accuracy: number}|null} Null
 *   when the student answered no solo time at all — the report says nothing
 *   rather than showing 0/0.
 */
export const soloAccuracy = (questionsDetail = []) => {
  const answered = questionsDetail.filter((q) => q?.result && q.result !== "no_answer");
  if (answered.length === 0) return null;

  const correct = answered.filter((q) => q.result === "correct").length;
  return {
    correct,
    answered: answered.length,
    accuracy: (correct / answered.length) * 100,
  };
};

// The bold title the fraction sits behind. A heading like "Headline:" or
// "Focus:", not pill content — the pill carries the numbers alone.
export const SOLO_ACCURACY_TITLE = "Solo Accuracy:";

/**
 * The pill's counts, text and colour.
 *
 * `correct` and `answered` are the raw facts; each surface formats its own
 * string from them. `fraction` is what goes inside the pill, and only that:
 * the words in front of it are the bullet's title, rendered outside.
 *
 * @param {Array<{result: string}>} questionsDetail Per-question results.
 * @returns {{correct: number, answered: number, fraction: string, tone: string,
 *   accuracy: number}|null}
 */
export const soloAccuracyPill = (questionsDetail = []) => {
  const stats = soloAccuracy(questionsDetail);
  if (!stats) return null;

  return {
    correct: stats.correct,
    answered: stats.answered,
    fraction: `${stats.correct} of ${stats.answered}`,
    tone: pillTone(stats.accuracy),
    accuracy: stats.accuracy,
  };
};
